import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { isInIframe } from '@shared/utils/isInIframe';
import { useDisclosure } from '@withjoy/joykit';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { usePaidFeature } from '@shared/hooks/usePaidFeature';
import { isVideoLayout } from '@shared/utils/photoPresentationLayout';

const useAutoopenHeaderUpgradeDialog = (shouldOpen: boolean, open: () => void) => {
  const hasPromptedDialog = useRef(false);

  useEffect(() => {
    if (shouldOpen && !hasPromptedDialog.current) {
      open();
      hasPromptedDialog.current = true;
    }
  }, [shouldOpen, open]);
};

const isPreviewing = isInIframe();

interface UsePremiumHeaderLayoutArgs
  extends Readonly<{
    headerPresentationLayout: SupportedHeaderPhotoLayoutSchema | undefined;
  }> {}

export const usePremiumHeaderLayout = (args: UsePremiumHeaderLayoutArgs) => {
  const { headerPresentationLayout } = args;

  const [dialogSecondaryAction, setDialogSecondaryAction] = useState<'turn-off' | undefined>(undefined);
  const headerUpgradeDialog = useDisclosure();

  const { isAdmin, hasIdentifiedUserOnce } = useEventUserRole();

  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  const isWebsitePublished = websitePublishCapabilityFeature.eventPublishedState.value;
  const isExposedToWebsiteVisibility = websitePublishCapabilityFeature.eventExposure.value;
  const isExposureLoading = websitePublishCapabilityFeature.eventExposure.isLoading;
  const isPublishedStateLoading = websitePublishCapabilityFeature.eventPublishedState.isLoading;

  const websiteVideoHeaderFeature = usePaidFeature('websiteVideoHeader');
  const hasPurchasedPremiumVideoBackground = websiteVideoHeaderFeature.value && !websiteVideoHeaderFeature.isLoading;
  const hasVideoLayout = isVideoLayout(headerPresentationLayout?.presentationLayout);
  const needsVideoBackgroundUpgrade = !hasPurchasedPremiumVideoBackground && hasVideoLayout;

  const handleUpgradeHeaderLayoutClick = useEventCallback(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({
        action: 'previewActionInteraction',
        source: 'joyPreview',
        value: 'upgradeHeaderButton',
      });
    } else {
      setDialogSecondaryAction(undefined);
      headerUpgradeDialog.onOpen();
    }
  });

  const showHeaderLayoutUpgradeBanner = useMemo(() => {
    if (isExposureLoading || isPublishedStateLoading) {
      return false;
    }

    return hasIdentifiedUserOnce && (isAdmin || isPreviewing) && (isWebsitePublished || !isExposedToWebsiteVisibility) && needsVideoBackgroundUpgrade;
  }, [hasIdentifiedUserOnce, isAdmin, isWebsitePublished, isExposedToWebsiteVisibility, needsVideoBackgroundUpgrade, isExposureLoading, isPublishedStateLoading]);

  useAutoopenHeaderUpgradeDialog(showHeaderLayoutUpgradeBanner && !isPreviewing && !headerUpgradeDialog.isOpen, () => {
    setDialogSecondaryAction('turn-off');
    headerUpgradeDialog.onOpen();
  });

  return {
    banner: {
      isVisible: showHeaderLayoutUpgradeBanner,
      onClick: handleUpgradeHeaderLayoutClick,
    },
    dialog: {
      isOpen: headerUpgradeDialog.isOpen,
      onOpen: headerUpgradeDialog.onOpen,
      onClose: headerUpgradeDialog.onClose,
      secondaryAction: dialogSecondaryAction,
    },
  } as const;
};

export const PremiumHeaderLayoutContext = createContext<ReturnType<typeof usePremiumHeaderLayout>>({
  banner: {
    isVisible: false,
    onClick: () => {},
  },
  dialog: {
    isOpen: false,
    onOpen: () => {},
    onClose: () => {},
    secondaryAction: undefined,
  },
});

export const usePremiumHeaderLayoutContext = () => {
  return useContext(PremiumHeaderLayoutContext);
};
